import { useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Play, Pause, Square, RotateCcw, Users } from "lucide-react";
import TimerDisplay from "./TimerDisplay";
import { installShortcutListeners } from "@/utils/timerShortcuts";
import { unlockAudio } from "@/utils/timerSounds";
import { useI18n } from "@/context/I18nContext";

import { API } from '../../utils/apiBase';

const DEFAULT_TOTAL_ROUNDS = 3;

export const BattleTimerPanel = ({ battle, athlete1, athlete2, timer, onUpdate }) => {
  const { t } = useI18n();
  const battleId = battle?.id;
  const totalRounds = Number(timer?.rounds) || DEFAULT_TOTAL_ROUNDS;

  const initIfNeeded = async () => {
    if (!battleId) return;
    if (!timer || timer.status === "idle" || !timer.first_athlete) {
      await axios.post(`${API}/battle/${battleId}/timer/init`);
      if (onUpdate) onUpdate();
    }
  };
  useEffect(() => { initIfNeeded(); /* eslint-disable-next-line */ }, [battleId]);

  const choose = async (firstAthlete) => {
    unlockAudio();
    await axios.post(`${API}/battle/${battleId}/timer/choose-first`, { first_athlete: firstAthlete });
    if (onUpdate) onUpdate();
  };
  const play = async () => {
    unlockAudio();
    await axios.post(`${API}/battle/${battleId}/timer/play`);
    if (onUpdate) onUpdate();
  };
  const pause = async () => {
    await axios.post(`${API}/battle/${battleId}/timer/pause`);
    if (onUpdate) onUpdate();
  };
  const stop = async () => {
    await axios.post(`${API}/battle/${battleId}/timer/stop`);
    if (onUpdate) onUpdate();
  };
  const reset = async () => {
    await axios.post(`${API}/battle/${battleId}/timer/reset`);
    if (onUpdate) onUpdate();
  };
  const setRound = async (round, athleteInRound) => {
    await axios.post(`${API}/battle/${battleId}/timer/set-round`, {
      round, athlete_in_round: athleteInRound || "first",
    });
    if (onUpdate) onUpdate();
  };
  const setAthlete = async (currentAthlete) => {
    await axios.post(`${API}/battle/${battleId}/timer/set-athlete`, {
      current_athlete: currentAthlete,
    });
    if (onUpdate) onUpdate();
  };

  // Keyboard shortcuts
  useEffect(() => {
    if (!timer) return;
    const handlers = {
      play: () => {
        if (timer.status === "running") pause();
        else if (timer.status === "paused" || timer.status === "pre_start") play();
      },
      stop: () => {
        if (timer.status === "running" || timer.status === "paused") stop();
      },
      reset: () => reset(),
      escape: () => {
        if (timer.status === "running") pause();
      },
    };
    return installShortcutListeners(handlers);
    // eslint-disable-next-line
  }, [timer?.status, battleId]);

  if (!battle) return null;

  const status = timer?.status || "idle";
  const currentName = timer?.current_athlete === "a1" ? athlete1?.name : timer?.current_athlete === "a2" ? athlete2?.name : null;

  return (
    <Card className="p-5 glass border-2 border-primary/30" data-testid="battle-timer-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-2xl font-bold text-foreground">⏱️ {t('timer.battleTimer')}</h3>
        {timer && timer.first_athlete && status !== "done" && (
          <div className="text-base text-muted-foreground" data-testid="battle-timer-round-indicator">
            {t('timer.round')} <span className="text-foreground font-bold">{timer.round}</span> / {totalRounds}
            {currentName && <> • <span className="text-primary font-bold">{currentName}</span></>}
          </div>
        )}
      </div>

      {/* Choose first athlete */}
      {(!timer || !timer.first_athlete || timer.status === "idle") && (
        <div className="space-y-3">
          <p className="text-base text-muted-foreground text-center">{t('timer.whoStarts')}</p>
          <div className="grid grid-cols-2 gap-3">
            <Button onClick={() => choose("a1")} size="lg" className="h-20 text-xl" data-testid="timer-choose-a1">
              {athlete1?.name || "Athlete 1"}
            </Button>
            <Button onClick={() => choose("a2")} size="lg" className="h-20 text-xl" data-testid="timer-choose-a2">
              {athlete2?.name || "Athlete 2"}
            </Button>
          </div>
        </div>
      )}

      {/* Timer + controls */}
      {timer && timer.first_athlete && status !== "done" && (
        <>
          <div className="flex justify-center my-4">
            <TimerDisplay timer={timer} variant="panel" playSounds={false} showProgress={false} />
          </div>

          {/* Manual round + athlete selectors (in case of mistake) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div className="p-3 rounded-lg bg-muted/40 border border-border">
              <div className="text-xs text-muted-foreground mb-2 font-semibold uppercase tracking-wider">
                {t('timer.roundSelector')}
              </div>
              <div className="flex gap-2">
                {Array.from({ length: totalRounds }, (_, i) => i + 1).map((r) => (
                  <Button
                    key={r}
                    size="sm"
                    variant={timer.round === r ? "default" : "outline"}
                    onClick={() => setRound(r, timer.athlete_in_round)}
                    data-testid={`battle-set-round-${r}`}
                    className="flex-1"
                  >
                    Round {r}
                  </Button>
                ))}
              </div>
            </div>
            <div className="p-3 rounded-lg bg-muted/40 border border-border">
              <div className="text-xs text-muted-foreground mb-2 font-semibold uppercase tracking-wider">
                <Users size={12} className="inline mr-1" />
                {t('timer.athleteSelector')}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={timer.current_athlete === "a1" ? "default" : "outline"}
                  onClick={() => setAthlete("a1")}
                  data-testid="battle-set-athlete-a1"
                  className="flex-1 truncate"
                >
                  {athlete1?.name || "A1"}
                </Button>
                <Button
                  size="sm"
                  variant={timer.current_athlete === "a2" ? "default" : "outline"}
                  onClick={() => setAthlete("a2")}
                  data-testid="battle-set-athlete-a2"
                  className="flex-1 truncate"
                >
                  {athlete2?.name || "A2"}
                </Button>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 justify-center">
            {status === "running" ? (
              <Button onClick={pause} variant="outline" size="lg" data-testid="timer-pause">
                <Pause className="mr-2" size={20} /> {t('timer.pause')}
              </Button>
            ) : (
              <Button onClick={play} size="lg" className="bg-green-600 hover:bg-green-700" data-testid="timer-play">
                <Play className="mr-2" size={20} /> {status === "paused" ? t('timer.resume') : t('timer.go')}
              </Button>
            )}
            <Button onClick={stop} variant="destructive" size="lg" data-testid="timer-stop">
              <Square className="mr-2" size={20} /> {t('timer.stopNext')}
            </Button>
            <Button onClick={reset} variant="outline" size="lg" data-testid="timer-reset">
              <RotateCcw className="mr-2" size={20} /> {t('timer.resetSegment')}
            </Button>
          </div>
        </>
      )}

      {/* Done */}
      {status === "done" && (
        <div className="text-center py-3 text-green-600 dark:text-green-400 font-bold text-lg" data-testid="timer-done">
          ✅ {t('timer.roundsDone')}
        </div>
      )}
    </Card>
  );
};

export default BattleTimerPanel;
